@if (hasItems()) {
	<div class="panel-surface">
		<!-- Header (always visible; collapsed vs expanded differ by caret + summary) -->
		<button
			type="button"
			class="panel-header"
			(click)="toggleCollapsed()"
			[attr.aria-expanded]="!collapsed()"
		>
			<i class="pi" [class.pi-chevron-up]="collapsed()" [class.pi-chevron-down]="!collapsed()"></i>
			<span class="header-summary">
				@if (uploadService.isActive()) {
					<span>Uploading {{ uploadService.stats().done }}/{{ uploadService.stats().total }}...</span>
				} @else {
					<span>{{ uploadService.stats().done }} uploaded</span>
					@if (failedCount() > 0) {
						<span class="header-summary-fail">, {{ failedCount() }} failed</span>
					}
				}
			</span>

			<!-- Overall progress bar (tracks batch average) -->
			<div class="header-progress">
				<div
					class="header-progress-fill"
					[style.width.%]="uploadService.stats().progressOverall"
				></div>
			</div>

			<span class="header-percent">{{ uploadService.stats().progressOverall | number:'1.0-0' }}%</span>

			@if (isAllTerminal() && !canCancelAll()) {
				<!-- Dismiss ✕ shown when queue is drained and nothing active -->
				<button
					type="button"
					class="icon-btn icon-btn--muted"
					(click)="onDismiss(); $event.stopPropagation()"
					title="Dismiss"
					aria-label="Dismiss"
				>
					<i class="pi pi-times"></i>
				</button>
			}
		</button>

		<!-- Expanded body -->
		@if (!collapsed()) {
			<div class="panel-toolbar">
				@if (canCancelAll()) {
					<button type="button" class="toolbar-btn" (click)="onCancelAll()">Cancel all</button>
				}
			</div>

			<div class="panel-list">
				@for (row of rows(); track row.kind === 'group' ? 'g:' + row.groupId : 'i:' + row.item.id) {
					@if (row.kind === 'group') {
						<button
							type="button"
							class="item-row item-row--group"
							[class.item-row--group-active]="groupHasActive(row.children)"
							(click)="toggleGroup(row.groupId, $event)"
							[attr.aria-expanded]="isGroupExpanded(row.groupId)"
						>
							<i
								class="pi group-caret"
								[class.pi-chevron-right]="!isGroupExpanded(row.groupId)"
								[class.pi-chevron-down]="isGroupExpanded(row.groupId)"
							></i>
							<i class="item-icon" [class]="iconForCategory(row.groupCategory)"></i>

							<div class="item-main">
								<div class="item-name" [title]="row.groupName">
									{{ row.groupName }}
									<span class="group-count">· {{ groupDoneCount(row.children) }}/{{ row.children.length }} variants</span>
								</div>
								<div class="item-status">
									<div class="progress-bar">
										<div class="progress-fill" [style.width.%]="groupProgress(row.children)"></div>
									</div>
									<span class="status-text">{{ groupProgress(row.children) | number:'1.0-0' }}%</span>
									@if (groupErrorCount(row.children) > 0) {
										<span class="status-text status-text--error">{{ groupErrorCount(row.children) }} failed</span>
									}
								</div>
							</div>

							<div class="item-actions">
								@if (groupHasRetriableFailures(row.children)) {
									<button
										type="button"
										class="icon-btn icon-btn--accent"
										(click)="onRetryGroup(row.groupId, $event)"
										title="Retry failed variants"
										aria-label="Retry failed variants"
									>
										<i class="pi pi-refresh"></i>
									</button>
								}
								@if (groupHasActive(row.children)) {
									<button
										type="button"
										class="icon-btn icon-btn--muted"
										(click)="onCancelGroup(row.groupId, $event)"
										title="Cancel group"
										aria-label="Cancel group"
									>
										<i class="pi pi-times"></i>
									</button>
								}
							</div>
						</button>
					} @else {
						<div
							class="item-row"
							[class.item-row--child]="row.item.groupId"
							[class.item-row--error]="row.item.status() === 'error'"
							[class.item-row--done]="row.item.status() === 'done'"
							[class.item-row--cancelled]="row.item.status() === 'cancelled'"
						>
							<i class="item-icon" [class]="iconFor(row.item)"></i>

							<div class="item-main">
								<div class="item-name" [title]="row.item.file.name">
									@if (row.item.duplicateWarning) {
										<i
											class="pi pi-exclamation-triangle item-warn-icon"
											title="A file with this name already exists"
										></i>
									}
									{{ row.item.file.name }}
								</div>

								@switch (row.item.status()) {
									@case ('uploading') {
										<div class="item-status">
											<div class="progress-bar">
												<div class="progress-fill" [style.width.%]="row.item.progress()"></div>
											</div>
											<span class="status-text">{{ row.item.progress() | number:'1.0-0' }}%</span>
										</div>
									}
									@case ('probing') {
										<div class="item-status">
											<span class="status-text status-text--muted">Probing...</span>
										</div>
									}
									@case ('queued') {
										<div class="item-status">
											@if (row.item.retryAfterMs() !== null) {
												<span class="status-text status-text--warn">
													Retrying in {{ retrySecondsFor(row.item) }}s...
												</span>
											} @else {
												<span class="status-text status-text--muted">Queued</span>
											}
										</div>
									}
									@case ('done') {
										<div class="item-status">
											<i class="pi pi-check-circle status-icon--success"></i>
											<span class="status-text status-text--muted">Done</span>
										</div>
									}
									@case ('error') {
										<div class="item-status">
											<span class="status-text status-text--error" [title]="row.item.error() ?? ''">
												{{ row.item.error() ?? 'Failed' }}
											</span>
										</div>
									}
									@case ('cancelled') {
										<div class="item-status">
											<span class="status-text status-text--muted">Cancelled</span>
										</div>
									}
								}
							</div>

							<div class="item-actions">
								@if (row.item.status() === 'error' && !row.item.nonRetriable) {
									<button
										type="button"
										class="icon-btn icon-btn--accent"
										(click)="onRetryItem(row.item)"
										title="Retry"
										aria-label="Retry"
									>
										<i class="pi pi-refresh"></i>
									</button>
								} @else if (row.item.status() === 'queued' || row.item.status() === 'probing' || row.item.status() === 'uploading') {
									<button
										type="button"
										class="icon-btn icon-btn--muted"
										(click)="onCancelItem(row.item)"
										title="Cancel"
										aria-label="Cancel"
									>
										<i class="pi pi-times"></i>
									</button>
								}
							</div>
						</div>
					}
				}
			</div>

			<!-- Footer: batch actions -->
			@if (hasFailures() || isAllTerminal()) {
				<div class="panel-footer">
					@if (hasFailures()) {
						<button type="button" class="footer-btn footer-btn--accent" (click)="onRetryAll()">
							Retry all failed ({{ retriableFailedCount() }})
						</button>
					}
					@if (isAllTerminal()) {
						<button type="button" class="footer-btn" (click)="onDismiss()">Dismiss</button>
					}
				</div>
			}
		}
	</div>
}
